import React, { FC, useMemo } from 'react';

import { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import Link from 'buyside/components/presentation/Link/link';
import RequestAccessButton from 'buyside/components/presentation/RequestAccessButton/requestAccessButton';
import SubscribeCard from 'buyside/components/presentation/SubscribePage/SubscribeCard/subscribeCard';
import SubscribeFeaturesTable from 'buyside/components/presentation/SubscribePage/SubscribeFeaturesTable/subscribeFeaturesTable';

import { availablePlans, PaymentPlanValues, PlanInfo } from 'interfaces/payment/plans';

import styles from './subscribePageStyles.scss';

const subscriptionText: { [K in PaymentPlanValues]: PlanInfo } = {
  BUYSIDE_PRO: {
    title: 'PRO',
    price: 7500,
    type: 'per year',
    sellingPoints: [
      {
        'Research By The Buyside. For The Buyside.': [
          'Get direct access to research from your peers with skin in the game',
          'Strengthen your holdings with in-depth analysis on thousands of new investment ideas a year',
          'Unlock thousands of custom models, channel checks, and more'
        ]
      },
      {
        'Find Exactly What You Need, When You Need It': [
          'Cut through the noise and unlock actionable ideas with custom alerts',
          'Get insights into risks and returns through thoughtful commentary and analysis',
          'See what other investors are saying about trending stocks with our consensus data',
          'Assess research based on actual returns, not hype'
        ]
      },
      {
        "Discover the Financial World's Top Alpha Generators": [
          "Grow your portfolio while tracking your peers that continually outperform the market in SumZero's fully transparent network",
          'Develop new investment strategies based on top fund moneymakers'
        ]
      },
      {
        'Networking Opportunities': [
          'Connect directly with hiring managers and seize opportunities for career growth',
          'Discuss top research ideas in a professional, engaging environment',
          'Empower your professional potential by posting investment ideas'
        ]
      }
    ],
    name: 'buysidePro'
  },
  RETAIL_TIER_1: {
    title: 'ELITE',
    price: 7500,
    type: 'per year',
    sellingPoints: [
      {
        'Annual Subscription': [
          'Comprehensive access to the entire research database.',
          'Special offers and exclusive features only available for buyside and Elite members.',
          'Real-time access without delay.'
        ]
      }
    ],
    name: 'retailTier1'
  },
  RETAIL_TIER_2: {
    title: 'PREMIUM',
    price: 1500,
    type: 'per bundle',
    sellingPoints: [
      {
        'Pay As You Go': [
          '35 pre-paid credits that never expire.',
          'Use one credit per idea to gain access to the full report.',
          'Add more credits at any time.',
          'The 45-day delayed access still applies.'
        ]
      }
    ],
    name: 'retailTier2'
  },
  RETAIL_TIER_3: {
    title: 'LITE',
    price: 350,
    type: 'per bundle',
    sellingPoints: [
      {
        'Pay As You Go': [
          '5 pre-paid credits that never expire.',
          'Use one credit per idea to gain access to the full report.',
          'Add more credits at any time.',
          'The 45-day delayed access still applies.'
        ]
      }
    ],
    name: 'retailTier3'
  },
  EVENT: {
    title: 'EVENT',
    price: 200,
    type: 'per bundle',
    sellingPoints: [],
    name: 'event'
  }
};

const SubscribePage: FC<{ isRetail: boolean }> = ({ isRetail }) => {
  const plans = useMemo(
    () =>
      isRetail
        ? availablePlans.retail.map(plan => subscriptionText[plan])
        : availablePlans.buyside.map(plan => subscriptionText[plan]),
    [isRetail]
  );

  return (
    <div className={styles.subscribePage}>
      <div className={styles.subscribePageHeader}>
        <h2>
          <strong>Access SumZero's Buyside Research</strong>
        </h2>
        <p>Join SumZero and gain access to top-quality research by professional investors</p>
      </div>
      <div className={styles.subscribePageCards}>
        {plans.map((plan, index) => (
          <ErrorBoundary key={`subscribe-card-${index}`} pageName="the checkout page">
            <SubscribeCard {...plan} />
          </ErrorBoundary>
        ))}
      </div>
      {isRetail ? (
        <div className={styles.subscribePageFeaturesTableContainer}>
          <h2>
            <strong>What You Get</strong>
          </h2>
          <SubscribeFeaturesTable />
        </div>
      ) : null}
      <div className={styles.subscribePageRequestInformation}>
        <h2>
          <strong>Need More Information?</strong>
        </h2>
        <p>
          Contact <Link to="mailto:access@sumzero.com">access@sumzero.com</Link> for any questions
          related to setting up on-going access to the SumZero research database.
        </p>
        <br />
        <RequestAccessButton text="Request More Information" type={BUTTON_TYPES.PRIMARY} />
      </div>
    </div>
  );
};

export default SubscribePage;
